/**
 * Presentational component showing run metadata in a 3-column grid.
 */

import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import ColumnLayout from "@cloudscape-design/components/column-layout"
import Container from "@cloudscape-design/components/container"
import Header from "@cloudscape-design/components/header"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { runTypeLabel } from "@/utils/runLabels"
import type { GetRunResponseContent } from "./types"

interface RunOverviewGridProps {
  /**
   * The run data to display.
   */
  run: GetRunResponseContent
}

function getStatusColor(status: string): "blue" | "green" | "red" {
  switch (status) {
    case "COMPLETED":
      return "green"
    case "FAILED":
      return "red"
    default:
      return "blue"
  }
}

/**
 * Displays run metadata fields (ID, file name, status, type, timestamps) in a grid layout.
 */
export default function RunOverviewGrid({ run }: RunOverviewGridProps) {
  return (
    <Container header={<Header variant="h3">Overview</Header>}>
      <ColumnLayout columns={3} variant="text-grid">
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">Run ID</Box>
          <Box fontWeight="normal">
            <code>{run.runId}</code>
          </Box>
        </SpaceBetween>
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">File Name</Box>
          <Box>{run.fileName || "Unknown"}</Box>
        </SpaceBetween>
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">Status</Box>
          <Badge color={getStatusColor(run.status)}>{run.status}</Badge>
        </SpaceBetween>
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">Type</Box>
          <Badge color={run.dryRun ? "blue" : "green"}>{runTypeLabel(run.dryRun)}</Badge>
        </SpaceBetween>
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">Created</Box>
          <Box>{new Date(run.createdAt).toLocaleString()}</Box>
        </SpaceBetween>
        {run.lastModifiedAt && (
          <SpaceBetween size="xxs">
            <Box variant="awsui-key-label">Last Modified</Box>
            <Box>{new Date(run.lastModifiedAt).toLocaleString()}</Box>
          </SpaceBetween>
        )}
        {run.dataStreamId && (
          <SpaceBetween size="xxs">
            <Box variant="awsui-key-label">Data Stream ID</Box>
            <Box fontWeight="normal">
              <code>{run.dataStreamId}</code>
            </Box>
          </SpaceBetween>
        )}
      </ColumnLayout>
    </Container>
  )
}
